import { useState } from "react";

import { api, type SafeUser } from "../lib/api";

interface SetupPageProps {
  onComplete: () => void;
}

export function SetupPage({ onComplete }: Readonly<SetupPageProps>) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post<SafeUser>("/api/v1/setup", { username, password });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Setup failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen w-screen grid-cols-1 lg:grid-cols-[1fr_minmax(380px,500px)]">
      {/* HERO panel */}
      <aside className="relative hidden flex-col overflow-hidden lg:flex">
        <img
          src="/assets/litmus-cover-photo.png"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0 bg-litmus-dark-green-900/72"
          aria-hidden
        />

        <div className="h-[30vh]" />

        <div className="relative px-10 pb-12">
          <div className="relative pb-2">
            <img
              src="/assets/Litmus_Logo_Reverse.png"
              alt="Litmus"
              className="h-12 w-auto object-contain"
            />
          </div>
          <h1 className="font-display text-[52px] font-bold leading-[1.05] tracking-tight text-white">
            Catalog Agent
          </h1>

          <div className="mt-8 border-t border-white/10 pt-8">
            <h2 className="font-display text-[30px] leading-[1.12] tracking-tight text-white">
              Catalog every{" "}
              <span className="text-litmus-light-green-400">tag</span>,
              <br />
              from <span className="italic">every</span> system,
              <br />
              on <span className="text-litmus-light-green-400">every</span>{" "}
              plant floor.
            </h2>
            <p className="mt-4 max-w-md text-[13.5px] leading-relaxed text-white/55">
              On-prem connector for the Litmus Catalog Agent. Crawls local PLCs,
              SCADA, historians, and databases — ships the metadata up the wire.
            </p>
            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-white/30">
              v1 · {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </aside>

      {/* FORM panel */}
      <div className="grid place-items-center bg-paper px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="mb-6 lg:hidden">
            <img
              src="/assets/Litmus_Logo.png"
              alt="Litmus"
              className="h-8 w-auto object-contain"
            />
          </div>

          <div className="eyebrow">First-run setup</div>
          <h1 className="mt-2 font-display text-[28px] font-semibold leading-tight tracking-tight text-ink">
            Create the administrator
          </h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-ash">
            One account is all the agent needs to start. You'll be able to add
            more users from the dashboard once you sign in.
          </p>

          <div className="mt-8 space-y-5">
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Username
              </span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
                className="input"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Password
              </span>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={12}
                  className="input pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-ash hover:text-ink"
                >
                  {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
              <span className="mt-1.5 block text-[11.5px] text-ash">
                Twelve characters minimum, scored ≥ 3 by zxcvbn — long
                unusual phrases beat short complicated ones.
              </span>
            </label>
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Confirm password
              </span>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="input pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  tabIndex={-1}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  aria-pressed={showConfirm}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-ash hover:text-ink"
                >
                  {showConfirm ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
            </label>
          </div>

          {error ? (
            <div className="mt-4 rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            className="btn-primary mt-6 w-full justify-center py-2.5"
          >
            {busy ? "Creating account…" : "Create administrator →"}
          </button>
        </form>
      </div>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[18px] w-[18px]"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[18px] w-[18px]"
      aria-hidden="true"
    >
      <path d="M3 3l18 18" />
      <path d="M10.58 10.58a2 2 0 0 0 2.83 2.83" />
      <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61C4.13 8.36 2 12 2 12a13.16 13.16 0 0 0 1.67 2.68A10.94 10.94 0 0 0 12 19" />
    </svg>
  );
}
